import { MessageSquare, MessageSquareOff } from "lucide-react";
import {
  SMS_PANEL_STATES, SMS_CONSENT_TEXT, smsPanelControls, smsPanelCopy,
} from "../memberPhone.js";
import { t } from "../strings.js";

// P3 — **THE PANEL SHE READS AND THE TWO CONTROLS SHE TAPS.**
//
// Hookless (R7/R8's entry requirement), so `scripts/p3-sms-settings.mjs` CALLS it once per state and
// reads the tree back. The state and the async steps live in `SmsSettings.tsx`, which is the
// `PushSettings`/`PushSettingsPanel` split one preference over and for the same reason: a decision
// written inside a stateful component is a decision no gate can run (R8/R95).
//
// **THE FACE SAYS WHAT IS; THE BUTTON SAYS WHAT A TAP DOES** (ITEM 7's law, which its neighbour is
// built on). The sentence reports whether a number is on file and whether texts are on; the buttons
// are *Save my number* / *Update my number* / *Remove my number*, never a toggle labelled with the
// thing it is already showing.
//
// **WHICH CONTROLS EXIST IS ARITHMETIC OFF `smsPanelControls` RATHER THAN LAYOUT.** `unknown` draws
// neither, because a panel offering a Save while it is still reading her row invites a member who
// already gave her number to type it again; and REMOVE is drawn only where there is something to
// remove, or it is A2's dead box wearing an action.
export function SmsSettingsPanel({
  state, phone = "", consent = false, busy = false, note = "", language = "en",
  onPhone, onConsent, onSave, onRemove,
}: {
  // R18's third state is a real value here and not a missing prop: `unknown` is what this panel
  // draws while her row is still coming back.
  state: string;
  phone?: string;
  consent?: boolean;
  busy?: boolean;
  note?: string;
  language?: string;
  onPhone: (value: string) => void;
  onConsent: (value: boolean) => void;
  onSave: () => void;
  onRemove: () => void;
}) {
  const copy = smsPanelCopy(state);
  const controls = smsPanelControls(state);
  const on = state === SMS_PANEL_STATES.ON;
  return (
    <div className={`sms-settings sms-settings--${state}`}>
      <h3 className="sms-settings__head">
        {on ? <MessageSquare size={15} aria-hidden="true" /> : <MessageSquareOff size={15} aria-hidden="true" />}
        {" "}{t(language, "sms.heading")}
      </h3>
      <p className="sms-settings__state">{t(language, copy.state)}</p>

      {controls.field ? (
        <>
          <label className="sms-settings__field">
            <span className="visually-hidden">{t(language, "sms.field")}</span>
            <input
              type="tel"
              name="smsSettingsNumber"
              autoComplete="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => onPhone(e.target.value)}
              // The SHAPE of a dialable number is one answer and not one per door, so this is the
              // signup field's own placeholder rather than a second example that could drift from
              // what the normaliser really accepts (R28/R29).
              placeholder={t(language, "signup.phonePlaceholder")}
              disabled={busy}
            />
          </label>

          {/* C8b's ONE consent sentence, IMPORTED rather than re-typed — three doors promising three
              different things is what that declaration exists to make impossible (R28/R29). It is
              UNCHECKED and it never blocks the save: a number given so the house can reach her about
              her own account is still a number, and marketing consent is a separate thing she gives
              on purpose. Ticking it here is what puts her in the SMS audience, which is her clause. */}
          <label className="consent-check sms-settings__consent">
            <input type="checkbox" checked={consent} onChange={(e) => onConsent(e.target.checked)} disabled={busy} />
            <span className="consent-check__text">{SMS_CONSENT_TEXT}</span>
          </label>

          {copy.save ? (
            <button type="button" className="sms-settings__act sms-settings__act--save" onClick={() => onSave()} disabled={busy}>
              {busy ? t(language, "sms.working") : t(language, copy.save)}
            </button>
          ) : null}
        </>
      ) : null}

      {/* HER CLAUSE 3. **NO CONFIRM, AND THAT IS HER OWN WORD RATHER THAN AN OMISSION** — *remove
          honours instantly, the same as STOP* — declared on R35's `NO_ASK_BY_DESIGN` with its
          reason. What it destroys is a number she can type again in the field directly above it,
          which is not the class `DeleteControl` exists for (a reading or a seal she paid for). */}
      {controls.remove ? (
        <button type="button" className="sms-settings__act sms-settings__act--remove" onClick={() => onRemove()} disabled={busy}>
          {busy ? t(language, "sms.working") : t(language, "sms.remove")}
        </button>
      ) : null}

      {/* R39/R47/R53/R61: the answer is READ and a refusal is SAID. A control that swallows its own
          failure leaves a truthful-looking screen, which is the one shape nobody reports. */}
      {note ? <p className="sms-settings__note sms-settings__note--warn" role="alert">{note}</p> : null}
    </div>
  );
}
